<template>
  <!-- same wrapper styles you used -->
  <div
    v-if="safe_questions.length"
    class="mt-3 ms-4 ps-3 border-start border-2 border-primary"
  >
    <div
      v-for="(dq, dq_index) in safe_questions"
      :key="dq.node_key || dq.model || dq.section_question_id || dq.id || dq_index"
      v-show="dq.is_visible"
      class="mb-3 rounded"
      :data-field-model="dq.model"
    >
      <!-- Question Label (same as yours) -->
      <label :for="dq.model" class="fw-medium text-xs text-start d-block mt-2">
        {{ dq_label(dq) }}
        <span v-if="dq_required(dq)" class="text-danger fs-5">*</span>
      </label>

      <!-- TEXT -->
      <input
        v-if="dq.type === 'text'"
        type="text"
        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
        :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
        :id="dq.model"
        :value="form_data[dq.model] || ''"
        @input="set_field(dq.model, $event.target.value, dq)"
        :placeholder="dq_label(dq)"
        :required="dq_required(dq) && dq.is_visible"
      />

      <!-- TEXTAREA -->
      <textarea
        v-else-if="dq.type === 'textarea'"
        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
        :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
        :id="dq.model"
        :value="form_data[dq.model] || ''"
        @input="set_field(dq.model, $event.target.value, dq)"
        :rows="3"
        :placeholder="dq_label(dq)"
        :required="dq_required(dq) && dq.is_visible"
      ></textarea>

      <!-- NUMBER -->
      <input
        v-else-if="dq.type === 'number'"
        type="text"
        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
        :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
        :id="dq.model"
        :value="form_data[dq.model] || ''"
        @input="on_number_input($event, dq)"
        :placeholder="dq_label(dq)"
        :required="dq_required(dq) && dq.is_visible"
      />

      <!-- ID -->
      <input
        v-else-if="dq.type === 'sa_id_number'"
        type="text"
        maxlength="13"
        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
        :class="{
          'border-danger':
            (submit_attempted && validation_errors[dq.model]) ||
            sa_id_validation_errors[dq.model]
        }"
        :id="dq.model"
        :value="form_data[dq.model] || ''"
        :placeholder="dq_label(dq)"
        :required="dq_required(dq) && dq.is_visible"
        @input="on_sa_id_input($event, dq)"
        @blur="on_sa_id_blur($event, dq)"
      />

      <!-- PHONE (single field with country code + number) -->
      <div v-else-if="dq.type === 'phone'">
        <input
          type="tel"
          maxlength="22"
          class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
          :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
          :id="dq.model"
          :value="form_data[dq.model] || ''"
          :placeholder="'+27 82 123 4567'"
          :required="dq_required(dq) && dq.is_visible"
          @input="on_dq_phone_input($event, dq)"
          @blur="on_dq_phone_blur($event, dq)"
        />
      </div>

      <!-- FAX -->
      <input
        v-else-if="dq.type === 'fax'"
        type="tel"
        maxlength="15"
        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
        :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
        :id="dq.model"
        :value="form_data[dq.model] || ''"
        :placeholder="'0XX XXX XXXX'"
        :required="dq_required(dq) && dq.is_visible"
        @input="on_contact_input($event, dq)"
        @blur="on_contact_blur($event, dq)"
      />

      <!-- EMAIL -->
      <input
        v-else-if="dq.type === 'email'"
        type="email"
        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
        :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
        :id="dq.model"
        :value="form_data[dq.model] || ''"
        :placeholder="'name@example.com'"
        :required="dq_required(dq) && dq.is_visible"
        @input="on_contact_input($event, dq)"
        @blur="on_contact_blur($event, dq)"
      />

      <!-- POSTAL CODE -->
      <input
        v-else-if="dq.type === 'postal_code'"
        type="text"
        maxlength="4"
        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
        :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
        :id="dq.model"
        :value="form_data[dq.model] || ''"
        :placeholder="'0000'"
        :required="dq_required(dq) && dq.is_visible"
        @input="on_contact_input($event, dq)"
        @blur="on_contact_blur($event, dq)"
      />

      <!-- PASSPORT NUMBER -->
      <input
        v-else-if="dq.type === 'passport_number'"
        type="text"
        maxlength="9"
        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
        :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
        :id="dq.model"
        :value="form_data[dq.model] || ''"
        :placeholder="'e.g. A01234567'"
        :required="dq_required(dq) && dq.is_visible"
        @input="on_contact_input($event, dq)"
        @blur="on_contact_blur($event, dq)"
      />

      <!-- DATE -->
      <input
        v-else-if="dq.type === 'date'"
        type="date"
        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
        :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
        :id="dq.model"
        :value="form_data[dq.model] || ''"
        @input="set_field(dq.model, $event.target.value, dq)"
        :required="dq_required(dq) && dq.is_visible"
      />

      <!-- FILE -->
      <div v-else-if="dq.type === 'file' || dq.type === 'pdf_file'">
        <div v-if="form_data[dq.model]">
          <div class="d-flex align-items-center gap-2">
            <span class="text-xs">{{ form_data[dq.model].file_name }}</span>

            <button
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
              type="button"
              @click="$emit('preview-file', dq.model)"
            >
              Preview
            </button>

            <button
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-white ms-2"
              type="button"
              @click="$emit('remove-file', dq.model)"
            >
              Remove
            </button>
          </div>
        </div>

        <div v-else>
          <input
            type="file"
            class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
            :id="dq.model"
            accept=".pdf"
            @change="$emit('file-upload', $event, dq.model, true)"
            :required="dq_required(dq)"
          />
          <small class="text-muted text-xxs">Only PDF files are accepted</small>

          <div v-if="file_validation_error?.[dq.model]" class="text-danger text-xxs mt-1">
            {{ file_validation_error[dq.model] }}
          </div>

          <div v-if="!form_data[dq.model]?.file" class="mt-2">
            <label class="text-xs" :for="dq.model + '_file_comment'">
              Please explain why you cannot upload this file:
            </label>

            <!--comment field WITHOUT v-model mutation -->
            <textarea
              :id="dq.model + '_file_comment'"
              class="form-control form-control-lg text-xs bg-light border-1 py-1 rounded-lg"
              :value="form_data[dq.model + '_file_comment'] || ''"
              @input="set_field(dq.model + '_file_comment', $event.target.value, dq)"
              rows="2"
              placeholder="Provide a reason..."
            ></textarea>
          </div>
        </div>
      </div>

      <!-- RADIO -->
      <div
        v-else-if="dq.type === 'single_select' || dq.type === 'radio'"
        class="radio-group mt-2 p-2 rounded"
        :class="{ 'border border-danger': submit_attempted && validation_errors[dq.model] }"
      >
        <label
          v-for="(option, opt_index) in (dq.options || [])"
          :key="'dq-radio-' + opt_index"
          class="d-flex align-items-center gap-2 mb-2 w-100"
          :for="`${dq.model}-${opt_index}`"
        >
          <input
            class="form-check-input position-static m-0 mt-1"
            type="radio"
            :name="dq.model"
            :id="`${dq.model}-${opt_index}`"
            :checked="is_checked_radio(dq.model, option)"
            @change="set_field(dq.model, option.value || option.text, dq, true)"
            :required="dq_required(dq) && dq.is_visible"
          />
          <span class="form-check-label text-xxs mb-0 flex-grow-1">
            {{ option.text }}
          </span>
        </label>
      </div>

      <!-- CHECKBOX -->
      <div
        v-else-if="dq.type === 'multi_select' || dq.type === 'checkboxes'"
        class="mt-2 p-2 rounded"
        :class="{ 'border border-danger': submit_attempted && validation_errors[dq.model] }"
      >
        <label
          v-for="(option, opt_index) in (dq.options || [])"
          :key="'dq-checkbox-' + opt_index"
          class="d-flex align-items-center gap-2 mb-2 w-100"
          :for="`${dq.model}-${opt_index}`"
        >
          <input
            class="form-check-input position-static m-0 mt-1"
            type="checkbox"
            :id="`${dq.model}-${opt_index}`"
            :checked="is_checked_checkbox(dq.model, option)"
            @change="toggle_checkbox(dq, option)"
          />
          <span class="form-check-label text-xxs mb-0 flex-grow-1">
            {{ option.text }}
          </span>
        </label>
      </div>

      <!-- FALLBACK -->
      <input
        v-else
        type="text"
        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
        :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
        :id="dq.model"
        :value="form_data[dq.model] || ''"
        @input="set_field(dq.model, $event.target.value, dq)"
        :placeholder="dq_label(dq)"
        :required="dq_required(dq) && dq.is_visible"
      />

      <!-- ERROR MESSAGE -->
      <div
        v-if="submit_attempted && validation_errors[dq.model]"
        class="text-danger text-xxs mt-1"
      >
        {{ validation_errors[dq.model] }}
      </div>

      <div
        v-else-if="sa_id_validation_messages[dq.model]"
        class="text-danger text-xxs mt-1"
      >
        {{ sa_id_validation_messages[dq.model] }}
      </div>

      <RecursiveDependentQuestions
        v-if="children(dq).length"
        :questions="children(dq)"
        :form_data="form_data"
        :validation_errors="validation_errors"
        :submit_attempted="submit_attempted"
        :file_validation_error="file_validation_error"
        :update_dependent_questions_visibility="update_dependent_questions_visibility"
        :sa_id_validation_errors="sa_id_validation_errors"
        :sa_id_validation_messages="sa_id_validation_messages"
        :sa_id_validation_details="sa_id_validation_details"
        @update-field="$emit('update-field', $event)"
        @number-input="$emit('number-input', $event.event, $event.node)"
        @file-upload="$emit('file-upload', $event)"
        @preview-file="$emit('preview-file', $event)"
        @remove-file="$emit('remove-file', $event)"
        @validate-sa-id-on-input="$emit('validate-sa-id-on-input', $event)"
        @validate-sa-id-on-blur="$emit('validate-sa-id-on-blur', $event)"
        @validate-contact-on-input="$emit('validate-contact-on-input', $event)"
        @validate-contact-on-blur="$emit('validate-contact-on-blur', $event)"
      />
    </div>
  </div>
</template>

<script>
import { parse_single_field_phone_input } from '@/utils/validations/contact_validators';

export default {
  name: "RecursiveDependentQuestions",
  props: {
    questions: { type: Array, default: () => [] },
    form_data: { type: Object, required: true }, // still a prop, but we never mutate it
    validation_errors: { type: Object, default: () => ({}) },
    file_validation_error: { type: Object, default: () => ({}) },
    submit_attempted: { type: Boolean, default: false },
    update_dependent_questions_visibility: { type: Function, default: null },

    //SA ID validation props
    sa_id_validation_errors: { type: Object, default: () => ({}) },
    sa_id_validation_messages: { type: Object, default: () => ({}) },
    sa_id_validation_details: { type: Object, default: () => ({}) },
  },
  emits: [
    "update-field", "number-input",
    "file-upload", "preview-file",
    "remove-file","validate-sa-id-on-input",
    "validate-sa-id-on-blur",
    "validate-contact-on-input",
    "validate-contact-on-blur"],
  computed: {

    safe_questions() {
      return Array.isArray(this.questions) ? this.questions.filter(Boolean) : [];
    },
  },
  methods: {
    children(node) {
      const list = node?.dependent_questions || node?.dependant_questions || [];
      return Array.isArray(list) ? list.filter(Boolean) : [];
    },
    dq_label(dq) {
      return dq?.label || dq?.question_text || "Question";
    },
    dq_required(dq) {
      return (dq?.required ?? dq?.is_mandatory) === true;
    },

    //single way to update parent data
    set_field(key, value, node = null, trigger_visibility = false) {
      this.$emit("update-field", { key, value });

      // if you want visibility logic to react on dependent answers too
      if (trigger_visibility && typeof this.update_dependent_questions_visibility === "function" && node) {
        this.update_dependent_questions_visibility(node, value);
      }
    },

    on_number_input(event, node) {
      // let parent sanitize value (your existing handleNumberInput)
      this.$emit("number-input", { event, node });
      // after parent sanitizes and sets, you're good
    },

    on_sa_id_input(event, node) {
      const sanitized_value = String(event?.target?.value || "").replace(/\D/g, "").slice(0, 13);
      this.set_field(node.model, sanitized_value, node);
      if (event?.target) {
        event.target.value = sanitized_value;
      }
      this.$emit("validate-sa-id-on-input", node);
    },

    on_sa_id_blur(event, node) {
      const sanitized_value = String(event?.target?.value || this.form_data?.[node.model] || "")
        .replace(/\D/g, "")
        .slice(0, 13);
      this.set_field(node.model, sanitized_value, node);
      if (event?.target) {
        event.target.value = sanitized_value;
      }
      this.$emit("validate-sa-id-on-blur", node);
    },

    on_contact_input(event, node) {
      const value = event?.target?.value || "";
      this.set_field(node.model, value, node);
      this.$emit("validate-contact-on-input", node);
    },

    on_contact_blur(event, node) {
      const value = event?.target?.value || this.form_data?.[node.model] || "";
      this.set_field(node.model, value, node);
      this.$emit("validate-contact-on-blur", node);
    },

    parse_dq_phone_input(raw_value) {
      return parse_single_field_phone_input(raw_value);
    },

    on_dq_phone_input(event, node) {
      const parsed = this.parse_dq_phone_input(event?.target?.value || '');
      this.set_field(node.model, parsed.formatted, node);
      if (event?.target) {
        event.target.value = parsed.formatted;
      }
      this.$emit("validate-contact-on-input", node);
    },

    on_dq_phone_blur(event, node) {
      const parsed = this.parse_dq_phone_input(event?.target?.value || this.form_data?.[node.model] || '');
      this.set_field(node.model, parsed.formatted, node);
      if (event?.target) {
        event.target.value = parsed.formatted;
      }
      this.$emit("validate-contact-on-blur", node);
    },

    is_checked_radio(model, option) {
      const val = this.form_data?.[model];
      const optVal = option?.value || option?.text;
      return val === optVal;
    },

    is_checked_checkbox(model, option) {
      const arr = this.form_data?.[model];
      const optVal = option?.value || option?.text;
      return Array.isArray(arr) && arr.includes(optVal);
    },

    toggle_checkbox(dq, option) {
      const model = dq.model;
      const optVal = option?.value || option?.text;
      const current = this.form_data?.[model];
      const next = Array.isArray(current) ? [...current] : [];

      const idx = next.indexOf(optVal);
      if (idx >= 0) next.splice(idx, 1);
      else next.push(optVal);

      this.set_field(model, next, dq, true);
    },
  },
};
</script>
